// Sugalarm — client-claimed transport events, cycle 431.
//
// The 2026-09-10 diagnostic could not say which phones froze, when, for
// how long, or whether a reopen was a thaw burst or a cold reload, because
// nothing records client transport. This is that record — posted on the
// next activation report (`collectActivationReport`), where the SERVER
// validates the kind against its closed list, forces the actor to
// `client_claimed`, stamps its own clock (ours goes to `details.client_ts`),
// keeps the first 20 per report and rate-limits by the authenticated
// identity. Everything here is a CLAIM, printed under its own heading in
// the episode report and never treated as server fact.
//
// Bounded and drained: at most `MAX_BUFFERED` are kept (cycle 433: the
// oldest row of the MOST NUMEROUS kind is dropped, not the oldest row),
// and `takeClientEvents` hands the first `MAX_PER_REPORT` to the report
// and forgets them — a failed post loses them, by design (a claim is not
// worth a retry queue).

export type ClientEventKind =
  | "wake_reopen"
  | "watchdog_reopen"
  | "stale_slot_retry"
  // Cycle 447: this device rendered a custody prompt on a surface. Claimed,
  // not server fact — the server issues a prompt and cannot see whether
  // anything drew it; this is what lets the episode report flag a prompt
  // issued that no surface reported rendering before its deadline.
  | "prompt_rendered";

export interface ClientEvent {
  kind: ClientEventKind;
  client_ts: string;
  details?: Record<string, string | number | boolean | null>;
}

export const MAX_BUFFERED = 50;
export const MAX_PER_REPORT = 20;

const buffer: ClientEvent[] = [];

/** ⚠ CYCLE 433 — EVICT THE OLDEST ROW OF THE MOST NUMEROUS KIND, NOT THE
 *  OLDEST ROW. A plain FIFO looks fair and is not: cycle 433 gave the
 *  watchdog three more states, and a server whose WebSocket handshake hangs
 *  produces a `watchdog_reopen` every ~15 s. Fifty of those inside one
 *  15-minute report interval would `shift()` out the `wake_reopen` rows
 *  that are the whole reason this rail exists — during exactly the
 *  transport fault it was built to diagnose. Dropping from the most
 *  numerous kind instead means a flood of any one kind can only ever crowd
 *  out ITSELF, and the true totals are on the activation report's `note`
 *  either way. Ties go to the oldest row, so the rule is deterministic. */
function evictOne(): void {
  const counts = new Map<string, number>();
  for (const e of buffer) counts.set(e.kind, (counts.get(e.kind) ?? 0) + 1);
  let worst = "";
  let most = -1;
  for (const [kind, n] of counts) {
    if (n > most) {
      most = n;
      worst = kind;
    }
  }
  const i = buffer.findIndex((e) => e.kind === worst);
  buffer.splice(i < 0 ? 0 : i, 1);
}

/** Record one claim. Never throws; details are scalars only. */
export function recordClientEvent(
  kind: ClientEventKind,
  details?: Record<string, string | number | boolean | null | undefined>,
  now: () => number = Date.now,
): void {
  try {
    const clean: Record<string, string | number | boolean | null> = {};
    for (const [k, v] of Object.entries(details ?? {})) {
      if (v === undefined) continue;
      if (typeof v === "string") clean[k] = v.slice(0, 64);
      else clean[k] = v;
    }
    buffer.push({ kind, client_ts: new Date(now()).toISOString(), details: clean });
    while (buffer.length > MAX_BUFFERED) evictOne();
  } catch {
    /* a claim must never break the transport that makes it */
  }
}

/** The events for the next report (oldest first), removed from the buffer. */
export function takeClientEvents(max: number = MAX_PER_REPORT): ClientEvent[] {
  return buffer.splice(0, Math.max(0, max));
}

export function peekClientEvents(): ClientEvent[] {
  return buffer.slice();
}

/** Test seam. */
export function resetClientEvents(): void {
  buffer.length = 0;
}
